/**
 * Healthcare & LTC editor (spec §11). ACA estimate vs. simple override for
 * pre-65; Medicare-side costs; optional per-person long-term care events
 * (defaulted off — an event, not a certainty).
 */
import type { LongTermCareAssumption, Plan } from '../model/types';
import { DEFAULT_HEALTHCARE } from '../engine/healthcare';
import { ACA_CONSTANTS_AS_OF } from '../engine/data/aca';
import { CurrencyField, NumberField } from './Inputs';
import { TIPS } from './tips';
import { fmtCompactUsd } from './format';

interface Props {
  plan: Plan;
  update: (mut: (p: Plan) => void) => void;
}

const CARE_LABELS: Record<LongTermCareAssumption['careType'], string> = {
  in_home: 'In-home care',
  assisted_living: 'Assisted living',
  nursing_home: 'Nursing home',
  memory_care: 'Memory care',
};

function ensureHc(p: Plan) {
  p.healthcare ??= structuredClone(DEFAULT_HEALTHCARE);
  return p.healthcare;
}

export function HealthcareTab({ plan, update }: Props) {
  const hc = plan.healthcare ?? DEFAULT_HEALTHCARE;
  const people = plan.household.people;

  return (
    <div>
      <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Before Medicare (retirement to 65)
      </h3>
      <p className="hint" style={{ marginTop: 0 }}>
        For anyone retiring before 65 this is usually the largest controllable expense. The ACA
        estimate uses your projected income vs. the poverty level — and for 2026 the enhanced
        subsidies have expired, so the <strong>400%-of-FPL cliff is back</strong>: one extra
        dollar of income can cost thousands in credit. Constants dated {ACA_CONSTANTS_AS_OF}.
      </p>
      <div className="seg" role="group" aria-label="Pre-Medicare coverage mode" style={{ marginBottom: 'var(--space-4)' }}>
        <button aria-pressed={hc.preMedicareMode === 'aca'}
          onClick={() => update((p) => { ensureHc(p).preMedicareMode = 'aca'; })}>
          ACA marketplace estimate
        </button>
        <button aria-pressed={hc.preMedicareMode === 'override'}
          onClick={() => update((p) => { ensureHc(p).preMedicareMode = 'override'; })}>
          I'll pay a fixed amount
        </button>
      </div>
      <div className="inputs-grid">
        {hc.preMedicareMode === 'aca' ? (
          <CurrencyField label="Benchmark silver premium ($/mo per person)" tip={TIPS.acaBenchmark}
            value={hc.benchmarkMonthlyPremium}
            hint="~$1,350/mo at 60-64 nationally; varies ~3x by state — check healthcare.gov"
            onCommit={(v) => update((p) => { ensureHc(p).benchmarkMonthlyPremium = v; })} />
        ) : (
          <CurrencyField label="My premium ($/mo per person)"
            value={hc.overrideMonthlyPremium}
            hint="Retiree coverage, COBRA, a spouse's plan, health share…"
            onCommit={(v) => update((p) => { ensureHc(p).overrideMonthlyPremium = v; })} />
        )}
        <CurrencyField label="Out-of-pocket ($/yr per person)"
          value={hc.oopAnnualPerPerson}
          hint="Deductibles, copays — before and after 65"
          onCommit={(v) => update((p) => { ensureHc(p).oopAnnualPerPerson = v; })} />
      </div>

      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Medicare (65+)
      </h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Part B ($202.90/mo base) and Part D ($38.99/mo base) are automatic, plus the IRMAA
        surcharge when income two years earlier crossed a threshold — those are cliffs, and the
        projection models the two-year lookback.
      </p>
      <div className="inputs-grid">
        <CurrencyField label="Medigap / Advantage ($/mo per person)"
          value={hc.medigapMonthly}
          onCommit={(v) => update((p) => { ensureHc(p).medigapMonthly = v; })} />
        <CurrencyField label="Dental + vision ($/yr per person)"
          value={hc.dentalVisionAnnual}
          onCommit={(v) => update((p) => { ensureHc(p).dentalVisionAnnual = v; })} />
      </div>

      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Long-term care
      </h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Modeled as an optional event, not a certainty — toggle it per person to see what a care
        need would do to the plan. When both spouses need care at once, plans usually break;
        better to see it here than live it unprepared.
      </p>
      {people.map((person) => {
        const ev = (plan.ltcEvents ?? []).find((e) => e.personId === person.id);
        return (
          <details key={person.id} className="item-card" open={!!ev?.enabled}>
            <summary>
              <strong>{person.label}</strong>
              <span className="sub">
                {ev?.enabled
                  ? `${CARE_LABELS[ev.careType]} at ${ev.onsetAge} for ${ev.durationYears}y · ${fmtCompactUsd(ev.annualCostToday)}/yr today`
                  : 'no care event modeled'}
              </span>
            </summary>
            <div className="inputs-grid">
              <div className="field">
                <label>
                  Model a care event?
                  <select value={ev?.enabled ? 'yes' : 'no'}
                    onChange={(e) => update((p) => {
                      p.ltcEvents ??= [];
                      let target = p.ltcEvents.find((x) => x.personId === person.id);
                      if (!target) {
                        target = {
                          personId: person.id, enabled: false, onsetAge: 85,
                          durationYears: 3, annualCostToday: 110_000,
                          careType: 'assisted_living',
                        };
                        p.ltcEvents.push(target);
                      }
                      target.enabled = e.target.value === 'yes';
                    })}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </label>
              </div>
              {ev?.enabled && (
                <>
                  <NumberField label="Care starts at age" value={ev.onsetAge} min={60} max={100}
                    onCommit={(v) => update((p) => {
                      p.ltcEvents!.find((x) => x.personId === person.id)!.onsetAge = v;
                    })} />
                  <NumberField label="Duration (years)" value={ev.durationYears} min={1} max={15}
                    onCommit={(v) => update((p) => {
                      p.ltcEvents!.find((x) => x.personId === person.id)!.durationYears = v;
                    })} />
                  <CurrencyField label="Annual cost (today's $)" value={ev.annualCostToday}
                    hint="In-home ~$75k · assisted living ~$70-110k · nursing home ~$120k+"
                    onCommit={(v) => update((p) => {
                      p.ltcEvents!.find((x) => x.personId === person.id)!.annualCostToday = v;
                    })} />
                  <div className="field">
                    <label>
                      Care type
                      <select value={ev.careType}
                        onChange={(e) => update((p) => {
                          p.ltcEvents!.find((x) => x.personId === person.id)!.careType =
                            e.target.value as LongTermCareAssumption['careType'];
                        })}>
                        {(Object.keys(CARE_LABELS) as LongTermCareAssumption['careType'][]).map((c) => (
                          <option key={c} value={c}>{CARE_LABELS[c]}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="field">
                    <label>
                      LTC insurance?
                      <select value={ev.insurance ? 'yes' : 'no'}
                        onChange={(e) => update((p) => {
                          const target = p.ltcEvents!.find((x) => x.personId === person.id)!;
                          target.insurance = e.target.value === 'yes'
                            ? {
                                dailyBenefit: 200, benefitPeriodYears: 3,
                                eliminationPeriodDays: 90, inflationRider: 0.03,
                                annualPremium: 3_500,
                              }
                            : undefined;
                        })}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </label>
                  </div>
                  {ev.insurance && (
                    <>
                      <CurrencyField label="Daily benefit" value={ev.insurance.dailyBenefit}
                        onCommit={(v) => update((p) => {
                          p.ltcEvents!.find((x) => x.personId === person.id)!.insurance!.dailyBenefit = v;
                        })} />
                      <NumberField label="Benefit period (years)" min={1} max={10}
                        value={ev.insurance.benefitPeriodYears}
                        onCommit={(v) => update((p) => {
                          p.ltcEvents!.find((x) => x.personId === person.id)!.insurance!.benefitPeriodYears = v;
                        })} />
                      <CurrencyField label="Annual premium" value={ev.insurance.annualPremium}
                        onCommit={(v) => update((p) => {
                          p.ltcEvents!.find((x) => x.personId === person.id)!.insurance!.annualPremium = v;
                        })} />
                    </>
                  )}
                </>
              )}
            </div>
          </details>
        );
      })}
    </div>
  );
}
